import { BaseApiClient } from './BaseApiClient';

interface PhoneStatus {
  phoneNumber: string | null;
  verified: boolean;
  message?: string;
}

interface PhoneAvailability {
  available: boolean;
  phoneNumber: string;
  message: string;
}

interface VerificationResponse {
  success: boolean;
  message: string;
  phoneNumber?: string;
  expiresIn?: number;
}

interface VerifyResponse {
  success: boolean;
  verified: boolean;
  message: string;
}

export class ProfileApiClient extends BaseApiClient {
  /**
   * Get current phone number and verification status
   * @deprecated Use the main /profile endpoint which includes telephone field
   * The /profile/phone endpoint does not exist - phone data is in profile.telephone
   */
  async getPhoneNumber(): Promise<PhoneStatus> {
    console.warn('getPhoneNumber() is deprecated. Phone data should be retrieved from /api/v1/profile endpoint in the telephone field.');
    // Return default status - this method should not be used
    return {
      phoneNumber: null,
      verified: false,
      message: 'This method is deprecated - use profile.telephone instead'
    };
  }

  /**
   * Set or update phone number
   */
  async setPhoneNumber(phoneNumber: string): Promise<PhoneStatus> {
    try {
      const response = await this.put<PhoneStatus>('/profile/phone', {
        phoneNumber
      });
      return response;
    } catch (error: any) {
      console.error('Failed to set phone number:', error);
      throw error;
    }
  }

  /**
   * Remove phone number from account
   */
  async removePhoneNumber(): Promise<{ message: string }> {
    try {
      const response = await this.delete<{ message: string }>('/profile/phone');
      return response;
    } catch (error: any) {
      console.error('Failed to remove phone number:', error);
      throw error;
    }
  }

  /**
   * Check if a phone number is available (not registered to another user)
   */
  async checkPhoneAvailability(phoneNumber: string): Promise<PhoneAvailability> {
    try {
      // URL encode the phone number to handle the + sign
      const encodedPhone = encodeURIComponent(phoneNumber);
      const response = await this.get<PhoneAvailability>(`/phone/check-availability/${encodedPhone}`);
      return response;
    } catch (error: any) {
      console.error('Failed to check phone availability:', error);
      throw error;
    }
  }

  /**
   * Send verification code to current phone number
   */
  async sendPhoneVerification(): Promise<VerificationResponse> {
    try {
      const response = await this.post<VerificationResponse>('/profile/phone/send-verification', {});
      return response;
    } catch (error: any) {
      console.error('Failed to send verification code:', error);
      throw error;
    }
  }

  /**
   * Verify phone number with code
   */
  async verifyPhoneNumber(code: string): Promise<VerifyResponse> {
    try {
      const response = await this.post<VerifyResponse>('/profile/phone/verify', {
        code
      });
      return response;
    } catch (error: any) {
      console.error('Failed to verify phone number:', error);
      throw error;
    }
  }

  /**
   * Get user profile (existing method)
   */
  async getProfile(): Promise<any> {
    try {
      const response = await this.get('/profile');
      return response;
    } catch (error) {
      console.error('Failed to get profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile (existing method)
   */
  async updateProfile(profileData: any): Promise<any> {
    try {
      const response = await this.put('/profile', profileData);
      return response;
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  }

  /**
   * Partial update for current user profile (PATCH /api/v1/profile).
   * Used for nested `settings.mmMoney` (MM$ issuer artwork) and other partial fields.
   */
  async patchProfile(partial: Record<string, unknown>): Promise<any> {
    try {
      return await this.patch('/profile', partial);
    } catch (error) {
      console.error('Failed to patch profile:', error);
      throw error;
    }
  }

  /**
   * Get all currency balances for current user
   * Uses /api/v1/profile?fields=balances endpoint
   * Returns {balances: {USD: 2.09, EUR: 50.00, ...}}
   */
  async getBalances(): Promise<Record<string, number>> {
    try {
      console.log('🔍 ProfileApiClient.getBalances() - Making API call to /profile?fields=balances');
      console.log('🔍 Current token:', this.getToken() ? `${this.getToken()?.substring(0, 20)}...` : 'NO TOKEN');
      
      const response = await this.get<{ balances: Record<string, number> }>('/profile?fields=balances');
      
      console.log('🔍 ProfileApiClient.getBalances() - Raw API response:', response);
      
      if (response && typeof response === 'object' && 'balances' in response) {
        const balances = response.balances;
        console.log('✅ Extracted balances:', balances);
        console.log('🔍 Balances keys:', Object.keys(balances));
        console.log('🔍 Balances values:', Object.values(balances));
        console.log('🔍 Has any non-zero balances:', Object.values(balances).some(v => v > 0));
        
        // Return balances object directly: {USD: 2.09, EUR: 50.00, ...}
        return balances || {};
      } else {
        console.warn('⚠️ ProfileApiClient.getBalances() - Unexpected response format, returning empty balances');
        console.warn('⚠️ Response:', response);
        return {};
      }
    } catch (error) {
      console.error('❌ ProfileApiClient.getBalances() - Failed to get balances:', error);
      throw error;
    }
  }

  /**
   * Get balance for a specific currency
   * Uses getBalances() and extracts the specific currency balance
   * Returns {balance: number, currency: string}
   */
  async getBalance(currency: string): Promise<{ balance: number; currency: string }> {
    try {
      const balances = await this.getBalances();
      const balance = balances[currency] || 0;
      return { balance, currency };
    } catch (error) {
      console.error(`❌ ProfileApiClient.getBalance() - Failed to get balance for ${currency}:`, error);
      throw error;
    }
  }

  /**
   * Add a role to the current user
   * Allowed self-service roles: MERCHANT, DEVELOPER
   * Endpoint: POST /api/v1/profile/roles/add
   */
  async addUserRole(role: 'MERCHANT' | 'DEVELOPER'): Promise<{ message: string; role: string }> {
    try {
      const response = await this.post<{ message: string; role: string }>('/profile/roles/add', {
        role
      });
      return response;
    } catch (error) {
      console.error(`Failed to add role ${role}:`, error);
      throw error;
    }
  }

  /**
   * Remove a role from the current user
   * Allowed self-service roles: MERCHANT, DEVELOPER
   * Endpoint: POST /api/v1/profile/roles/remove
   */
  async removeUserRole(role: 'MERCHANT' | 'DEVELOPER'): Promise<{ message: string; role: string }> {
    try {
      const response = await this.post<{ message: string; role: string }>('/profile/roles/remove', {
        role
      });
      return response;
    } catch (error) {
      console.error(`Failed to remove role ${role}:`, error);
      throw error;
    }
  }
}